import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GlassCard } from '../components/LiquidComponents';

// Shared text styles to keep the policy consistent and readable on dark/light/nardo themes.
// Monochrome link treatment (no blue): matches the site's neutral link style.
const linkClass =
  'text-zinc-900 dark:text-white hover:underline underline-offset-2 transition-colors break-words';
const paragraphClass =
  'text-zinc-600 dark:text-zinc-300 leading-7 md:leading-8 font-light text-[15px] md:text-base';

const Strong: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <strong className="font-semibold text-zinc-800 dark:text-zinc-100">{children}</strong>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-xl md:text-2xl font-semibold text-zinc-900 dark:text-white mt-12 mb-4 scroll-mt-28">
    {children}
  </h2>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-base md:text-lg font-semibold text-zinc-800 dark:text-zinc-100 mt-7 mb-2">
    {children}
  </h3>
);

const MailLink: React.FC<{ email: string }> = ({ email }) => (
  <a href={`mailto:${email}`} className={linkClass}>
    {email}
  </a>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
    {children}
  </a>
);

const ListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="text-zinc-600 dark:text-zinc-300 leading-7 font-light text-[15px] md:text-base">
    {children}
  </li>
);

export const Privacy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-36 md:pt-44 pb-24 min-h-screen container mx-auto px-4 md:px-6">
      <div className="max-w-3xl mx-auto">
        {/* Back link — same uppercase treatment as the project detail page */}
        <button
          onClick={() => navigate('/')}
          className="mb-8 flex items-center gap-2 text-zinc-400 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white [.nardogrey_&]:text-white transition-colors text-sm uppercase tracking-wider font-medium cursor-pointer w-fit p-2 -ml-2 rounded-lg hover:bg-white/5 active:scale-95"
        >
          <ArrowLeft size={16} />
          <span>Back to Home</span>
        </button>

        <GlassCard hoverEffect={false} className="p-6 sm:p-8 md:p-12">
          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-zinc-900 dark:text-white">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm md:text-base text-zinc-500 dark:text-zinc-400">
            <Strong>Last updated: 28 June 2026</Strong>
          </p>

          {/* Intro */}
          <div className="mt-8 space-y-5">
            <p className={paragraphClass}>
              This Privacy Policy explains how personal data is collected, used, and shared when you
              visit <Strong>helenendorfsky.tech</Strong> (the &ldquo;Site&rdquo;), operated by Ruhlan
              Jafarli (&ldquo;I&rdquo;, &ldquo;me&rdquo;, &ldquo;my&rdquo;). I take your privacy
              seriously and only collect data that is necessary to operate this Site and respond to
              you.
            </p>
            <p className={paragraphClass}>
              This Site is operated from Munich, Germany, and this policy is written to comply with
              the EU General Data Protection Regulation (GDPR).
            </p>
          </div>

          {/* 1. Who is responsible */}
          <SectionHeading>1. Who is responsible for your data</SectionHeading>
          <p className={paragraphClass}>
            The person responsible for processing your personal data (&ldquo;data
            controller&rdquo;) is:
          </p>
          <div className="mt-4 pl-4 border-l-2 border-zinc-300 dark:border-white/20 space-y-1 text-zinc-600 dark:text-zinc-300 font-light text-[15px] md:text-base">
            <p>
              <Strong>Ruhlan Jafarli (helenendorfsky)</Strong>
            </p>
            <p>Munich, Germany</p>
            <p>
              Email: <MailLink email="helenendorfsky@gmail.com" /> or{' '}
              <MailLink email="ruhlan@sevenoir.com" />
            </p>
          </div>
          <p className={`${paragraphClass} mt-5`}>
            If you have any questions about this policy or how your data is handled, you can reach me
            at either email above.
          </p>

          {/* 2. What data I collect */}
          <SectionHeading>2. What data I collect and why</SectionHeading>
          <p className={paragraphClass}>
            I only collect personal data when you actively choose to provide it. I do <Strong>not</Strong>{' '}
            sell your data, and I do <Strong>not</Strong> use it for advertising.
          </p>

          <SubHeading>a) Contact form (&ldquo;Let&rsquo;s Talk&rdquo;)</SubHeading>
          <p className={paragraphClass}>When you use the general contact form, I collect:</p>
          <ul className="mt-3 list-disc pl-6 space-y-2">
            <ListItem>
              <Strong>Name</Strong>
            </ListItem>
            <ListItem>
              <Strong>Email address</Strong>
            </ListItem>
            <ListItem>
              <Strong>Your message</Strong>
            </ListItem>
          </ul>
          <p className={`${paragraphClass} mt-4`}>
            <Strong>Why:</Strong> to read and respond to your enquiry.
          </p>
          <p className={paragraphClass}>
            <Strong>Legal basis (GDPR Art. 6(1)(b) and (f)):</Strong> to take steps at your request
            and my legitimate interest in responding to people who contact me.
          </p>

          <SubHeading>
            b) Investment deck request form (&ldquo;Request Investment Deck&rdquo;)
          </SubHeading>
          <p className={paragraphClass}>
            If you request access to investment materials, I collect:
          </p>
          <ul className="mt-3 list-disc pl-6 space-y-2">
            <ListItem>
              <Strong>Name</Strong>
            </ListItem>
            <ListItem>
              <Strong>Email address</Strong>
            </ListItem>
            <ListItem>
              <Strong>Organization / company</Strong>
            </ListItem>
            <ListItem>
              <Strong>The project you are interested in</Strong>
            </ListItem>
            <ListItem>
              <Strong>An optional message</Strong>
            </ListItem>
          </ul>
          <p className={`${paragraphClass} mt-4`}>
            <Strong>Why:</Strong> to evaluate and respond to your request for business or investment
            materials.
          </p>
          <p className={paragraphClass}>
            <Strong>Legal basis (GDPR Art. 6(1)(b) and (f)):</Strong> to take steps at your request
            and my legitimate interest in handling business enquiries.
          </p>

          <SubHeading>c) AI chat assistant (&ldquo;Digital Twin&rdquo;)</SubHeading>
          <p className={paragraphClass}>
            The Site includes an AI chat assistant. When you type a message into it:
          </p>
          <ul className="mt-3 list-disc pl-6 space-y-2">
            <ListItem>
              Your message and the recent conversation (up to the last 12 messages) are sent to{' '}
              <Strong>Google</Strong> to generate a reply, using Google&rsquo;s Gemini AI service.
            </ListItem>
          </ul>
          <p className={`${paragraphClass} mt-4`}>
            <Strong>Important:</Strong> I do not store your chat conversations on my systems.
            However, the text you type is sent to Google to produce the response. Please{' '}
            <Strong>do not enter sensitive personal information</Strong> into the chat. Your use of
            the chat is also subject to Google&rsquo;s Privacy Policy (
            <ExternalLink href="https://policies.google.com/privacy">
              https://policies.google.com/privacy
            </ExternalLink>
            ).
          </p>
          <p className={paragraphClass}>
            <Strong>Legal basis (GDPR Art. 6(1)(f)):</Strong> my legitimate interest in providing an
            interactive way to learn about my work.
          </p>

          <SubHeading>d) Usage analytics</SubHeading>
          <p className={paragraphClass}>
            I use <Strong>Vercel Web Analytics</Strong> to understand general, aggregated traffic to
            the Site (such as which pages are visited). This analytics is{' '}
            <Strong>privacy-friendly and cookieless</Strong> &mdash; it does not use cookies and does
            not track you across other websites or build a profile of you.
          </p>
          <p className={paragraphClass}>
            <Strong>Legal basis (GDPR Art. 6(1)(f)):</Strong> my legitimate interest in
            understanding how the Site is used so I can improve it.
          </p>

          {/* 3. Cookies */}
          <SectionHeading>3. Cookies</SectionHeading>
          <p className={paragraphClass}>
            This Site does <Strong>not</Strong> use tracking or advertising cookies. No cookies are
            set when you simply browse the Site. (A secure session token is only stored if you log in
            to the private administration area, which is not available to regular visitors.)
          </p>

          {/* 4. Who your data is shared with */}
          <SectionHeading>4. Who your data is shared with</SectionHeading>
          <p className={paragraphClass}>
            I do not sell your personal data. To operate the Site, I use a small number of trusted
            third-party service providers (&ldquo;data processors&rdquo;) who process data on my
            behalf:
          </p>

          <div className="mt-5 overflow-x-auto rounded-xl border border-black/10 dark:border-white/10">
            <table className="w-full min-w-[560px] text-left text-[14px] md:text-[15px] border-collapse">
              <thead>
                <tr className="bg-black/[0.03] dark:bg-white/[0.04]">
                  <th className="px-4 py-3 font-semibold text-zinc-800 dark:text-zinc-100 border-b border-black/10 dark:border-white/10">
                    Service
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-800 dark:text-zinc-100 border-b border-black/10 dark:border-white/10">
                    Purpose
                  </th>
                  <th className="px-4 py-3 font-semibold text-zinc-800 dark:text-zinc-100 border-b border-black/10 dark:border-white/10">
                    What it may receive
                  </th>
                </tr>
              </thead>
              <tbody className="text-zinc-600 dark:text-zinc-300 font-light align-top">
                <tr className="border-b border-black/5 dark:border-white/5">
                  <td className="px-4 py-3">
                    <Strong>Vercel Inc.</Strong>
                  </td>
                  <td className="px-4 py-3">Website hosting and cookieless analytics</td>
                  <td className="px-4 py-3">
                    Technical/usage data; processes (but does not store on my behalf) chat requests
                    passing through the Site
                  </td>
                </tr>
                <tr className="border-b border-black/5 dark:border-white/5">
                  <td className="px-4 py-3">
                    <Strong>Supabase</Strong>
                  </td>
                  <td className="px-4 py-3">Database</td>
                  <td className="px-4 py-3">
                    Contact form (&ldquo;Let&rsquo;s Talk&rdquo;) submissions: name, email, message
                  </td>
                </tr>
                <tr className="border-b border-black/5 dark:border-white/5">
                  <td className="px-4 py-3">
                    <Strong>Formspree</Strong>
                  </td>
                  <td className="px-4 py-3">Delivers contact form submissions to me by email</td>
                  <td className="px-4 py-3">
                    Both forms&rsquo; submissions (name, email, message; and for the deck form:
                    organization and project interest)
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3">
                    <Strong>Google (Gemini AI)</Strong>
                  </td>
                  <td className="px-4 py-3">Powers the AI chat assistant</td>
                  <td className="px-4 py-3">
                    The text you type into the chat and recent chat history
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className={`${paragraphClass} mt-5`}>
            Each of these providers has its own privacy practices. Some of them may process data on
            servers outside the European Economic Area (for example, in the United States); where
            that happens, such transfers are covered by appropriate safeguards (such as Standard
            Contractual Clauses) provided by those companies.
          </p>
          <p className={paragraphClass}>
            I may also disclose personal data if required to do so by law.
          </p>

          {/* 5. How long I keep your data */}
          <SectionHeading>5. How long I keep your data</SectionHeading>
          <ul className="mt-2 list-disc pl-6 space-y-2">
            <ListItem>
              <Strong>Contact and deck-request submissions:</Strong> kept only as long as needed to
              handle your enquiry and any reasonable follow-up, after which they are deleted.
            </ListItem>
            <ListItem>
              <Strong>Chat messages:</Strong> not stored by me (see Section 2c).
            </ListItem>
            <ListItem>
              <Strong>Analytics:</Strong> aggregated and not tied to your identity.
            </ListItem>
          </ul>
          <p className={`${paragraphClass} mt-4`}>
            You can ask me to delete your data at any time (see Section 6).
          </p>

          {/* 6. Your rights under GDPR */}
          <SectionHeading>6. Your rights under GDPR</SectionHeading>
          <p className={paragraphClass}>If you are in the EU/EEA, you have the right to:</p>
          <ul className="mt-3 list-disc pl-6 space-y-2">
            <ListItem>
              <Strong>Access</Strong> the personal data I hold about you;
            </ListItem>
            <ListItem>
              <Strong>Rectify</Strong> inaccurate or incomplete data;
            </ListItem>
            <ListItem>
              <Strong>Erase</Strong> your data (&ldquo;right to be forgotten&rdquo;);
            </ListItem>
            <ListItem>
              <Strong>Restrict</Strong> or <Strong>object</Strong> to processing;
            </ListItem>
            <ListItem>
              <Strong>Data portability</Strong> &mdash; receive your data in a usable format;
            </ListItem>
            <ListItem>
              <Strong>Withdraw consent</Strong> at any time, where processing is based on consent.
            </ListItem>
          </ul>
          <p className={`${paragraphClass} mt-4`}>
            To exercise any of these rights, email me at <MailLink email="helenendorfsky@gmail.com" />{' '}
            or <MailLink email="ruhlan@sevenoir.com" /> and I will respond as soon as reasonably
            possible.
          </p>
          <p className={paragraphClass}>
            You also have the right to lodge a complaint with a supervisory authority. In Germany,
            this is your local State Data Protection Authority (Landesdatenschutzbehörde); for
            Bavaria/Munich, the Bayerisches Landesamt für Datenschutzaufsicht (BayLDA).
          </p>

          {/* 7. Data security */}
          <SectionHeading>7. Data security</SectionHeading>
          <p className={paragraphClass}>
            I take reasonable technical measures to protect your data, including securing the
            Site&rsquo;s administration area behind authentication and restricting database access.
            However, no method of transmission over the internet is completely secure, and I cannot
            guarantee absolute security.
          </p>

          {/* 8. Children's privacy */}
          <SectionHeading>8. Children&rsquo;s privacy</SectionHeading>
          <p className={paragraphClass}>
            This Site is not directed at children under 16, and I do not knowingly collect personal
            data from them.
          </p>

          {/* 9. Changes to this policy */}
          <SectionHeading>9. Changes to this policy</SectionHeading>
          <p className={paragraphClass}>
            I may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
            at the top reflects the most recent version. Material changes will be reflected on this
            page.
          </p>

          {/* 10. Contact */}
          <SectionHeading>10. Contact</SectionHeading>
          <p className={paragraphClass}>For any privacy-related questions or requests, contact:</p>
          <div className="mt-4 pl-4 border-l-2 border-zinc-300 dark:border-white/20 space-y-1 text-zinc-600 dark:text-zinc-300 font-light text-[15px] md:text-base">
            <p>
              <Strong>Ruhlan Jafarli (helenendorfsky)</Strong>
            </p>
            <p>
              Email: <MailLink email="helenendorfsky@gmail.com" /> or{' '}
              <MailLink email="ruhlan@sevenoir.com" />
            </p>
            <p>Munich, Germany</p>
          </div>

          {/* Disclaimer */}
          <hr className="my-10 border-black/10 dark:border-white/10" />
          <p className="text-sm md:text-[15px] italic text-zinc-500 dark:text-zinc-400 leading-7 font-light">
            This Privacy Policy is provided for transparency and to reflect how this Site actually
            handles data. It is a good-faith template, not legal advice. If this Site grows into a
            larger commercial operation or begins collecting more sensitive data, you should have it
            reviewed by a qualified data-protection lawyer.
          </p>
        </GlassCard>
      </div>
    </div>
  );
};

export default Privacy;
